import { useState } from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Copy, Trash2 } from 'lucide-react';
import { ItemEditor } from './ItemEditor';
import { Badge } from '@/components/ui/badge';

interface ItemEditorCardProps {
  item: any;
  itemIndex: number;
  totalItems: number;
  onChange: (updatedItem: any) => void;
  onDuplicate: () => void;
  onDelete: () => void;
  onSave?: () => void;
  onCancel?: () => void;
}

export function ItemEditorCard({
  item,
  itemIndex,
  totalItems,
  onChange,
  onDuplicate,
  onDelete,
  onSave,
  onCancel
}: ItemEditorCardProps) {
  const [activeTab, setActiveTab] = useState('fields');

  return (
    <Card className="shadow-lg">
      <CardHeader className="pb-4">
        {/* Breadcrumb and Actions */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Structure /</span>
            <span className="font-semibold text-foreground">
              {item.label || 'Untitled item'}
            </span>
            <Badge variant="outline" className="ml-2">
              #{itemIndex + 1} of {totalItems}
            </Badge>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={onDuplicate}
              data-testid="button-duplicate-item"
            >
              <Copy className="w-4 h-4 mr-2" />
              Duplicate
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={onDelete}
              data-testid="button-delete-item"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger 
              value="fields"
              data-testid="tab-fields"
              aria-controls="tab-content-fields"
              aria-selected={activeTab === 'fields'}
            >
              Fields
            </TabsTrigger>
            <TabsTrigger 
              value="logic"
              data-testid="tab-logic"
              aria-controls="tab-content-logic"
              aria-selected={activeTab === 'logic'}
            >
              Logic
            </TabsTrigger>
            <TabsTrigger 
              value="meta"
              data-testid="tab-meta"
              aria-controls="tab-content-meta"
              aria-selected={activeTab === 'meta'}
            >
              Meta
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </CardHeader>

      <CardContent className="p-6">
        <Tabs value={activeTab}>
          <TabsContent value="fields" id="tab-content-fields">
            <div className="space-y-4">
              <ItemEditor item={item} onChange={onChange} />
            </div>
          </TabsContent>

          <TabsContent value="logic" id="tab-content-logic">
            <div className="space-y-4">
              {item.type === 'ynna_actions_review' && (
                <div className="space-y-4">
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <h4 className="font-medium mb-2">Scoring Configuration</h4>
                    <p className="text-sm text-muted-foreground mb-4">
                      Configure how responses are scored
                    </p>
                    <ItemEditor item={item} onChange={onChange} />
                  </div>
                </div>
              )}
              {item.type !== 'ynna_actions_review' && (
                <div className="text-center py-8 text-muted-foreground">
                  <p>Logic configuration not available for this item type</p>
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="meta" id="tab-content-meta">
            <div className="space-y-4">
              <div className="p-4 bg-muted/50 rounded-lg">
                <h4 className="font-medium mb-3">Item Metadata (Read-only)</h4>
                <div className="space-y-2 text-sm font-mono">
                  <div>
                    <span className="text-muted-foreground">ID:</span>{' '}
                    <span className="font-medium">{item.id}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Type:</span>{' '}
                    <span className="font-medium">{item.type}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Actions Enabled:</span>{' '}
                    <span className="font-medium">{item.actions_enabled ? 'Yes' : 'No'}</span>
                  </div>
                  {item.score?.enabled && (
                    <div>
                      <span className="text-muted-foreground">Scoring:</span>{' '}
                      <span className="font-medium">Enabled (Weight: {item.score.weight})</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>

        {/* Footer Actions */}
        {(onSave || onCancel) && (
          <div className="flex justify-end gap-3 mt-6 pt-6 border-t">
            {onCancel && (
              <Button
                variant="outline"
                onClick={onCancel}
                data-testid="button-cancel-editing"
              >
                Cancel
              </Button>
            )}
            {onSave && (
              <Button
                onClick={onSave}
                data-testid="button-save-item"
              >
                Save Changes
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
